import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';

import { fetchPurchaseOrderArrivalScheduleDetail } from '#/api/srm/purchaseOrder/fetchArrivalScheduleDetail';
import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import { AppDialog } from '#/modules/commonComponents/dialog';
import {
  dialogContentCompactGapClassName,
  dialogWideMaxWidthClassName,
} from '#/modules/commonComponents/dialog/dialogLayoutTokens';
import { DialogFooter } from '#/modules/commonComponents/dialog/DialogPrimitives';
import DetailTable from '#/modules/commonComponents/table/DetailTable';
import TruncatedTextWithTooltip from '#/modules/commonComponents/table/TruncatedTextWithTooltip';
import useDetailTable from '#/hooks/table/useDetailTable';
import ArrivalScheduleStatusBadge from '#/modules/srmMgmt/purchaseOrder/components/ArrivalScheduleStatusBadge';
import { getPurchaseOrderApiErrorMessage } from '#/modules/srmMgmt/purchaseOrder/helper/apiError';
import {
  canEditDraftArrivalSchedule,
} from '#/modules/srmMgmt/purchaseOrder/helper/arrivalScheduleStatus';
import { formatDateOrDash } from '#/utils/date';
import { formatDisplayQty } from '#/utils/formatNumber';
import { purchaseOrderArrivalScheduleDetailQueryKey } from '#/utils/queryKeys';

function dash(value) {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  return value;
}

function SummaryCell({ label, value }) {
  return (
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="mt-0.5 text-sm font-medium wrap-break-word text-gray-900">
        {dash(value)}
      </p>
    </div>
  );
}

SummaryCell.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

function mapScheduleLineRows(lines) {
  return (lines ?? []).map((line, index) => {
    const mtr = line?.mtr ?? line?.material;
    return {
      id: mtr?.id || line?.mtrId || line?.materialId || `schedule-line-${index}`,
      mtrNo: mtr?.mtrNo ?? mtr?.materialNo ?? '—',
      name: mtr?.name ?? '—',
      plannedQty: formatDisplayQty(line?.plannedQty),
      receivedQty: formatDisplayQty(line?.receivedQty),
      remainingQty: formatDisplayQty(line?.remainingQty),
    };
  });
}

const LINE_COLUMNS = [
  {
    id: 'mtrNo',
    header: '料號',
    accessorKey: 'mtrNo',
    size: 140,
    enableSorting: false,
    meta: { cellClassName: 'td_left' },
    cell: ({ row }) => (
      <TruncatedTextWithTooltip text={row.original.mtrNo} />
    ),
  },
  {
    id: 'name',
    header: '名稱',
    accessorKey: 'name',
    size: 180,
    enableSorting: false,
    meta: { cellClassName: 'td_left' },
    cell: ({ row }) => (
      <TruncatedTextWithTooltip text={row.original.name} />
    ),
  },
  {
    id: 'plannedQty',
    header: '計劃量',
    accessorKey: 'plannedQty',
    size: 90,
    enableSorting: false,
    meta: { cellClassName: 'td_right' },
  },
  {
    id: 'receivedQty',
    header: '已收量',
    accessorKey: 'receivedQty',
    size: 90,
    enableSorting: false,
    meta: { cellClassName: 'td_right' },
  },
  {
    id: 'remainingQty',
    header: '剩餘量',
    accessorKey: 'remainingQty',
    size: 90,
    enableSorting: false,
    meta: { cellClassName: 'td_right' },
  },
];

/**
 * 到貨排程預覽：表頭＋lines；Footer 僅關閉／可選 DRAFT 編輯。
 * Dialog 內不做 confirm／receive。
 */
const PurchaseOrderArrivalSchedulePreviewDialog = ({
  open,
  onOpenChange,
  purchaseOrderId,
  scheduleId,
}) => {
  const detailQuery = useQuery({
    queryKey: purchaseOrderArrivalScheduleDetailQueryKey(
      purchaseOrderId,
      scheduleId,
    ),
    queryFn: () => fetchPurchaseOrderArrivalScheduleDetail(
      purchaseOrderId,
      scheduleId,
    ),
    enabled: Boolean(open && purchaseOrderId && scheduleId),
  });

  const detail = detailQuery.data?.data;
  const status = detail?.status;
  const canEdit = canEditDraftArrivalSchedule(status)
    && purchaseOrderId
    && scheduleId;

  const warehouseLabel = detail?.warehouse
    ? ([detail.warehouse.code, detail.warehouse.name].filter(Boolean).join(' ')
      || '—')
    : '—';

  const description = (
    <div className="flex flex-wrap items-center gap-2">
      <span className="truncate text-sm font-medium">
        {formatDateOrDash(detail?.expectedArrivalDate)}
      </span>
      {status ? <ArrivalScheduleStatusBadge status={status} size="sm" /> : null}
    </div>
  );

  const lineRows = useMemo(
    () => (detail ? mapScheduleLineRows(detail.lines) : []),
    [detail],
  );

  const detailTableState = useDetailTable(lineRows, LINE_COLUMNS, {
    enableExpanding: false,
    getRowId: (row) => String(row.id),
  });

  const footer = (
    <DialogFooter>
      <Button
        type="button"
        variant="outline"
        onClick={() => onOpenChange(false)}
      >
        關閉
      </Button>
      {canEdit ? (
        <Button type="button" asChild>
          <Link
            to={ROUTES.purchaseOrderArrivalScheduleEdit(
              purchaseOrderId,
              scheduleId,
            )}
          >
            <Pencil className="size-3.5" aria-hidden />
            編輯
          </Link>
        </Button>
      ) : null}
    </DialogFooter>
  );

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title="到貨排程預覽"
      description={detail ? description : undefined}
      headerVariant="primary"
      showCloseButton
      maxWidthClassName={dialogWideMaxWidthClassName}
      contentClassName={dialogContentCompactGapClassName}
      footer={footer}
    >
      {detailQuery.isLoading ? (
        <p className="py-6 text-sm text-muted-foreground">載入中…</p>
      ) : null}
      {detailQuery.isError ? (
        <p className="py-6 text-sm text-destructive">
          {getPurchaseOrderApiErrorMessage(detailQuery.error, '載入排程失敗')}
        </p>
      ) : null}
      {detail && !detailQuery.isLoading ? (
        <div className="space-y-4">
          <div
            className={cn(
              'grid grid-cols-1 gap-x-4 gap-y-3',
              'sm:grid-cols-2',
            )}
          >
            <SummaryCell
              label="預計到貨日"
              value={formatDateOrDash(detail.expectedArrivalDate)}
            />
            <SummaryCell label="倉別" value={warehouseLabel} />
            <SummaryCell
              label="計劃量"
              value={formatDisplayQty(detail.plannedQty)}
            />
            <SummaryCell
              label="剩餘量"
              value={formatDisplayQty(detail.remainingQty)}
            />
            <SummaryCell label="備註" value={detail.remark} />
          </div>
          <div>
            <p className="mb-2 text-xs font-medium text-gray-500">明細</p>
            {lineRows.length === 0 ? (
              <p className="text-sm text-muted-foreground">尚無明細</p>
            ) : (
              <DetailTable
                state={detailTableState}
                containerClassName="max-h-72 max-w-full overflow-auto"
                tableClassName="detail-table--flat-lines"
              />
            )}
          </div>
        </div>
      ) : null}
    </AppDialog>
  );
};

PurchaseOrderArrivalSchedulePreviewDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  purchaseOrderId: PropTypes.string,
  scheduleId: PropTypes.string,
};

export default PurchaseOrderArrivalSchedulePreviewDialog;
